import { Chart, ChartConfiguration, registerables } from "chart.js";
import * as React from "react";

interface Props {
  ratio: number;
  options: {
    primaryColor: string;
    textColor: string;
  };
}

interface CenterTextInterface {
  text: string;
  color: string;
}

const DoughnutChart = (props: Props) => {
  const chartRef = React.useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = React.useRef<Chart<"doughnut"> | null>(null);

  const drawCenterTextPlugin = React.useCallback(
    (chart: Chart) => {
      const pluginOption = {
        centerText: {
          text: `${Math.floor(props.ratio * 100)}`,
          color: props.options.textColor,
        },
      };

      drawCenterText(chart, pluginOption);
    },
    [props.ratio, props.options.textColor],
  );

  React.useEffect(() => {
    if (!chartRef.current) {
      return;
    }
    const ctx = chartRef.current.getContext("2d");
    if (!ctx) {
      return;
    }
    Chart.register(...registerables);

    const { primaryColor } = props.options;
    const baseColor = "#EBECF0";
    const config: ChartConfiguration<"doughnut", number[]> = {
      type: "doughnut",
      data: {
        datasets: [
          {
            data: [props.ratio, 1.0 - props.ratio],
            backgroundColor: [primaryColor, baseColor],
            hoverBackgroundColor: [primaryColor, baseColor],
            borderWidth: 0,
          },
        ],
      },
      options: {
        aspectRatio: 2 / 1,
        cutout: "90%",
        plugins: {
          tooltip: { enabled: false },
          legend: { display: false },
        },
      },
      plugins: [
        {
          id: "chartjs-draw-center-text",
          beforeDraw: drawCenterTextPlugin,
        },
      ],
    };

    const chart = new Chart(ctx, config);
    chartInstanceRef.current = chart;

    return () => {
      if (chart && typeof chart.destroy === "function") {
        chart.destroy();
      }
    };
  }, [
    props.ratio,
    props.options.primaryColor,
    props.options.textColor,
    drawCenterTextPlugin,
  ]);

  React.useEffect(() => {
    const chart = chartInstanceRef.current;
    if (chart) {
      chart.update();
    }
  });

  return <canvas ref={chartRef} style={{ width: "100%" }} />;
};

const resetChart = (chart: Chart) => {
  const ctx = chart.ctx;

  if (!ctx) {
    return;
  }

  ctx.clearRect(
    chart.chartArea.left,
    chart.chartArea.top,
    chart.chartArea.right,
    chart.chartArea.bottom,
  );
};

const drawCenterText = (
  chart: Chart,
  options: { centerText: CenterTextInterface },
) => {
  const ctx = chart.ctx;

  if (!ctx) {
    return;
  }

  resetChart(chart);

  const { text, color } = options.centerText;
  const chartHeight = chart.chartArea.bottom;
  const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
  const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

  // 数字部分の表示
  const numbersFontSize = chartHeight / 4;
  ctx.font = `bold ${numbersFontSize}px YuGothic`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = color;
  ctx.fillText(text, centerX - 10, centerY);

  // %部分の表示
  const numbersPosition = centerX;
  const numbersWidth = ctx.measureText(text).width;
  const percentFontSize = numbersFontSize * 0.6;
  ctx.font = `bold ${percentFontSize}px YuGothic`;
  ctx.fillText(
    "%",
    numbersPosition + numbersWidth / 2,
    centerY + numbersFontSize * 0.1,
  );
};

export default DoughnutChart;
